import React, { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';

export type LeitnerTabId = 'due' | 'words' | 'add';

export const LEITNER_TAB_ORDER: LeitnerTabId[] = ['due', 'words', 'add'];

const TAB_INDICATOR: Record<LeitnerTabId, string> = {
  due: 'bg-amber-400 dark:bg-amber-300',
  words: 'bg-rose-400 dark:bg-rose-300',
  add: 'bg-emerald-400 dark:bg-emerald-300'
};

export function leitnerTabCarouselIndex(tabId: LeitnerTabId, isRtl: boolean): number {
  const idx = LEITNER_TAB_ORDER.indexOf(tabId);
  return isRtl ? LEITNER_TAB_ORDER.length - 1 - idx : idx;
}

export function leitnerTabCarouselOrder(isRtl: boolean): LeitnerTabId[] {
  return isRtl ? [...LEITNER_TAB_ORDER].reverse() : LEITNER_TAB_ORDER;
}

export interface LeitnerModalTabsProps {
  active: LeitnerTabId;
  onChange: (tab: LeitnerTabId) => void;
  dueCount?: number;
  dragOffsetPx?: number;
  isDragging?: boolean;
  carouselRef?: React.RefObject<HTMLDivElement | null>;
}

export const LeitnerModalTabs: React.FC<LeitnerModalTabsProps> = ({
  active,
  onChange,
  dueCount = 0,
  dragOffsetPx = 0,
  isDragging = false,
  carouselRef
}) => {
  const { t } = useTranslation('leitner');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const labelId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabNavRowRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<LeitnerTabId, HTMLButtonElement>>>({});
  const activeTabRef = useRef(active);
  const dragUiRef = useRef({ isDragging, dragOffsetPx });
  const isRtlRef = useRef(isRtl);
  const [indicatorStyle, setIndicatorStyle] = React.useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const tabVisualOrder = useCallback(
    (tabId: LeitnerTabId) => {
      const idx = LEITNER_TAB_ORDER.indexOf(tabId);
      return isRtl ? LEITNER_TAB_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );

  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  useEffect(() => {
    activeTabRef.current = active;
  }, [active]);

  useEffect(() => {
    dragUiRef.current = { isDragging, dragOffsetPx };
  }, [isDragging, dragOffsetPx]);

  const updateIndicator = useCallback(() => {
    const row = tabNavRowRef.current;
    if (!row) return;
    const tab = activeTabRef.current;
    const activeButton = tabRefs.current[tab];
    if (!activeButton) return;

    const { isDragging: dragging, dragOffsetPx: drag } = dragUiRef.current;
    const cards = carouselRef?.current;
    const cardWidth = cards?.clientWidth ?? 0;
    const idx = LEITNER_TAB_ORDER.indexOf(tab);
    const measureInRow = (btn: HTMLElement) => {
      const rowRect = row.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return { left: btnRect.left - rowRect.left, width: btnRect.width };
    };

    let { left, width } = measureInRow(activeButton);

    if (dragging && cardWidth > 0) {
      const progress = isRtlRef.current ? drag / cardWidth : -drag / cardWidth;
      let neighborIdx = idx;
      if (progress > 0 && idx < LEITNER_TAB_ORDER.length - 1) neighborIdx = idx + 1;
      else if (progress < 0 && idx > 0) neighborIdx = idx - 1;

      const neighborTab = LEITNER_TAB_ORDER[neighborIdx];
      const neighborButton = neighborTab ? tabRefs.current[neighborTab] : undefined;
      if (neighborButton && neighborIdx !== idx) {
        const amount = Math.min(1, Math.abs(progress));
        const neighbor = measureInRow(neighborButton);
        left = left + (neighbor.left - left) * amount;
        width = width + (neighbor.width - width) * amount;
      }
    }

    setIndicatorStyle((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
  }, [carouselRef]);

  useLayoutEffect(() => {
    updateIndicator();
    const btn = tabRefs.current[active];
    const nav = scrollRef.current;
    const needsTabNavScroll = nav != null && nav.scrollWidth > nav.clientWidth + 1;
    if (btn && needsTabNavScroll) {
      btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      queueMicrotask(() => updateIndicator());
      requestAnimationFrame(() => {
        requestAnimationFrame(() => updateIndicator());
      });
    }
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [active, updateIndicator, isRtl, dueCount]);

  useEffect(() => {
    const nav = scrollRef.current;
    const row = tabNavRowRef.current;
    if (!nav) return;
    const onScroll = () => updateIndicator();
    nav.addEventListener('scroll', onScroll, { passive: true });
    const observers: ResizeObserver[] = [];
    if (typeof ResizeObserver !== 'undefined') {
      if (row) {
        const rowObs = new ResizeObserver(() => updateIndicator());
        rowObs.observe(row);
        observers.push(rowObs);
      }
      LEITNER_TAB_ORDER.forEach((tabId) => {
        const btn = tabRefs.current[tabId];
        if (!btn) return;
        const btnObs = new ResizeObserver(() => updateIndicator());
        btnObs.observe(btn);
        observers.push(btnObs);
      });
    }
    return () => {
      nav.removeEventListener('scroll', onScroll);
      observers.forEach((o) => o.disconnect());
    };
  }, [updateIndicator, active, isRtl, t]);

  useLayoutEffect(() => {
    if (!isDragging && dragOffsetPx === 0) return;
    updateIndicator();
  }, [isDragging, dragOffsetPx, updateIndicator]);

  const activeIndicator = TAB_INDICATOR[active];

  return (
    <nav
      aria-labelledby={labelId}
      dir="ltr"
      data-no-tab-swipe
      className="shrink-0 border-b border-slate-200/90 bg-gradient-to-b from-white/80 to-slate-50/70 pb-0 dark:border-slate-700/80 dark:from-gray-900/70 dark:to-gray-950/80"
    >
      <p id={labelId} className="sr-only">
        {t('title')}
      </p>
      <div ref={scrollRef} className="w-full">
          <div ref={tabNavRowRef} role="tablist" className="relative flex w-full flex-nowrap items-end">
            {LEITNER_TAB_ORDER.map((tabId) => {
              const isActive = active === tabId;
              return (
                <button
                  key={tabId}
                  ref={(el) => {
                    if (el) tabRefs.current[tabId] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive ? true : false}
                  tabIndex={isActive ? 0 : -1}
                  data-tour-id={`leitner-tab-${tabId}`}
                  style={{ order: tabVisualOrder(tabId) }}
                  onClick={() => onChange(tabId)}
                  className={`relative flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 py-2.5 text-center transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 sm:py-3 ${
                    isActive
                      ? 'font-semibold text-slate-900 dark:text-white'
                      : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
                  }`}
                >
                  <span className="truncate text-xs font-semibold">{t(`tab.${tabId}`)}</span>
                  {tabId === 'due' && dueCount > 0 ? (
                    <span className="min-w-[1.1rem] shrink-0 rounded-full bg-slate-200 px-1.5 py-px text-[10px] font-bold leading-4 text-slate-700 dark:bg-slate-700 dark:text-slate-100">
                      {dueCount > 99 ? '99+' : dueCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <div
              className={`absolute -bottom-px h-0.5 rounded-full ${activeIndicator}`}
              style={{
                left: `${indicatorStyle.left}px`,
                width: `${indicatorStyle.width}px`,
                transition: isDragging ? 'none' : 'left 0.2s ease-out, width 0.2s ease-out',
                zIndex: 10
              }}
            />
          </div>
      </div>
    </nav>
  );
};
